import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  List,
  ArrowUp,
  Clock,
  Network,
  Building2,
  LineChart,
  ClipboardList,
  Table,
  ShieldCheck,
  Trophy,
  History,
  Users,
  Settings as SettingsIcon,
  Bell,
  PackageSearch,
  Truck,
  Wrench,
  ListChecks,
  Lock,
  PackagePlus,
  FileText,
  ShieldAlert,
  GitBranch,
  Inbox,
  CalendarClock,
  DoorOpen,
  Sliders,
  ScanLine,
  Search as SearchIcon,
  UserCheck,
} from "lucide-react";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { hasModuleAccess } from "../lib/moduleAccess";

const NavItem = ({ to, icon: Icon, label, badge, badgeColor, end, onClick, testId }) => {
  const location = useLocation();
  // Active = exact pathname + search match
  const [path, search = ""] = to.split("?");
  const currentSearch = location.search.replace(/^\?/, "");
  const isActive = location.pathname === path && currentSearch === search;
  return (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) =>
      "flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] relative " +
      (isActive
        ? "text-white"
        : "text-[#c5cad8] hover:text-white hover:bg-white/[0.04]")
    }
      style={{
        borderLeft: isActive ? "2px solid #185FA5" : "2px solid transparent",
        background: isActive ? "rgba(24,95,165,0.12)" : "transparent",
        fontFamily: "Syne, sans-serif",
        fontWeight: isActive ? 600 : 500,
      }}
    data-testid={testId || `nav-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <Icon size={14} strokeWidth={1.8} />
    <span className="flex-1">{label}</span>
    {badge && (
      <span
        style={{
          background: badgeColor === "red" ? "rgba(226,75,74,0.18)" : badgeColor === "amber" ? "rgba(243,156,18,0.18)" : "rgba(255,255,255,0.06)",
          border: `1px solid ${badgeColor === "red" ? "rgba(226,75,74,0.5)" : badgeColor === "amber" ? "rgba(243,156,18,0.5)" : "rgba(255,255,255,0.1)"}`,
          color: badgeColor === "red" ? "#ff7e7c" : badgeColor === "amber" ? "#f3b14a" : "#9aa1b3",
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          padding: "1px 6px",
          borderRadius: 3,
          fontWeight: 500,
        }}
      >
        {badge}
      </span>
    )}
  </NavLink>
  );
};

const SectionLabel = ({ children, accent }) => (
  <div
    className="px-4 pt-4 pb-1.5"
    style={{
      fontFamily: "DM Mono, monospace",
      fontSize: 9,
      letterSpacing: "1.5px",
      color: accent || "#9198b0",
      textTransform: "uppercase",
    }}
    data-testid={accent ? "sidebar-section-admin" : undefined}
  >
    {children}
  </div>
);

// Amber-accented variant for /admin/* links
const AdminNavItem = ({ to, icon: Icon, label, testId }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <NavLink
      to={to}
      className={
        "flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] relative " +
        (isActive ? "text-white" : "text-[#c5cad8] hover:text-white hover:bg-white/[0.04]")
      }
      style={{
        borderLeft: isActive ? "2px solid #f3b14a" : "2px solid transparent",
        background: isActive ? "rgba(243,177,74,0.10)" : "transparent",
        fontFamily: "Syne, sans-serif",
        fontWeight: isActive ? 600 : 500,
      }}
      data-testid={testId || `nav-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <Icon size={14} strokeWidth={1.8} color={isActive ? "#f3b14a" : undefined} />
      <span className="flex-1">{label}</span>
    </NavLink>
  );
};

const RiskFooter = () => {
  const { t } = useT();
  const [risk, setRisk] = useState(78);
  useEffect(() => {
    const id = setInterval(() => {
      const delta = (Math.random() - 0.5) * 4;
      setRisk((r) => {
        const next = Math.max(70, Math.min(86, 78 + delta));
        return Math.round(next);
      });
    }, 2800);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="m-3 p-3 rounded-md"
      style={{
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid="risk-index-footer"
    >
      <div
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 8,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        {t("nav.escalation_risk_index").toUpperCase()}
      </div>
      <div
        className="mt-1"
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 24,
          color: "#f3b14a",
          fontWeight: 500,
          lineHeight: 1,
        }}
      >
        {risk}
      </div>
      <div
        className="mt-2 relative rounded-full overflow-hidden"
        style={{
          height: 5,
          background: "linear-gradient(90deg, rgba(39,174,96,0.25) 0%, rgba(243,156,18,0.35) 50%, rgba(226,75,74,0.45) 100%)",
        }}
      >
        <div
          className="absolute top-0 bottom-0 left-0 rounded-full"
          style={{
            width: `${risk}%`,
            background: "linear-gradient(90deg, #27ae60 0%, #f39c12 55%, #E24B4A 100%)",
            transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </div>
      <div
        className="mt-2"
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          color: "#f3b14a",
          letterSpacing: "1px",
        }}
      >
        {t("nav.high_risk")}
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const isSidebarOpen = useStore((s) => s.isSidebarOpen);
  const setSidebarOpen = useStore((s) => s.setSidebarOpen);
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useT();
  const isPlatformAdmin = user && user.role === "platform_admin";
  const role = user?.role || "observer";
  const minResponder = ["responder", "escalation_manager", "tenant_admin", "platform_admin"].includes(role);
  const minEscMgr = ["escalation_manager", "tenant_admin", "platform_admin"].includes(role);

  // Iter143/144 — Időkapu-foglaló gates driven by USER ROLE, not tenant flag.
  // A forwarder without any dock system of their own must still see booking UI
  // (they book into other tenants' plants). Only the "Beállítások" sub-item
  // still requires the caller's own tenant to be a plant (dock_scheduling_enabled).
  const dockRoles = Array.isArray(user?.dock_roles) ? user.dock_roles : [];
  const dockEnabled = !!user?.dock_scheduling_enabled;
  const hasDockAdminConfig = isPlatformAdmin || (dockEnabled && dockRoles.includes("dock_admin"));
  const hasDockAdminRole = isPlatformAdmin || dockRoles.includes("dock_admin");
  const hasDockBooker = isPlatformAdmin || dockRoles.includes("dock_booker");
  const hasDockModule = hasDockAdminRole || hasDockBooker;

  // Close drawer on route change for mobile
  useEffect(() => {
    setSidebarOpen(false);
     
  }, [location.pathname, location.search]);

  const closeNav = () => setSidebarOpen(false);

  return (
    <>
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[249] backdrop-enter"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(2px)" }}
          onClick={closeNav}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={
          "fixed left-0 z-[250] flex flex-col transition-transform duration-[250ms] lg:translate-x-0 " +
          (isSidebarOpen ? "translate-x-0" : "-translate-x-full")
        }
        style={{
          top: "var(--topbar-h, 56px)",
          bottom: 0,
          width: 236,
          background: "#0d0f18",
          borderRight: "1px solid rgba(255,255,255,0.12)",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        data-testid="sidebar"
      >
        <nav className="flex-1 overflow-y-auto py-2">
          {minEscMgr && hasModuleAccess(user, "exec") && (
            <>
              <div className="px-3 pt-2 pb-1">
                <NavItem to="/exec" icon={Trophy} label={t("nav.executive_view")} testId="nav-executive-view" />
              </div>
              <div className="mx-4 my-2" style={{ height: 1, background: "rgba(255,255,255,0.10)" }} />
            </>
          )}

          {hasModuleAccess(user, "incidents") && (
            <>
              <SectionLabel>{t("nav.section_incidents")}</SectionLabel>
              <NavItem to="/" end icon={LayoutGrid} label={t("nav.dashboard")} testId="nav-dashboard" />
              <NavItem to="/countdown" icon={Clock} label="Next 24h" testId="nav-countdown" />
              <NavItem to="/incidents" icon={List} label={t("nav.all_incidents")} badge="14" badgeColor="red" testId="nav-all-incidents" />
              <NavItem to="/incidents?sev=s1" icon={ArrowUp} label={t("nav.s1_critical")} badge="3" badgeColor="red" testId="nav-s1-critical" />
              <NavItem to="/incidents?sla=breach" icon={Clock} label={t("nav.sla_breaching")} badge="2" badgeColor="amber" testId="nav-sla-breaching" />
            </>
          )}
          {!hasModuleAccess(user, "incidents") && (
            // Dashboard is cross-cutting — render alone when Incidents section is hidden.
            <>
              <NavItem to="/" end icon={LayoutGrid} label={t("nav.dashboard")} testId="nav-dashboard" />
              <NavItem to="/countdown" icon={Clock} label="Next 24h" testId="nav-countdown" />
            </>
          )}

          {(hasModuleAccess(user, "sorting") || hasModuleAccess(user, "tms") || hasModuleAccess(user, "engineering") || hasModuleAccess(user, "stock_lock") || hasModuleAccess(user, "material_movement")) && (
            <NavItem to="/service-requests" icon={ListChecks} label="Service Requests" testId="nav-service-requests" />
          )}

          {hasModuleAccess(user, "sorting") && (
            <NavItem to="/sorting" icon={PackageSearch} label={t("nav.sorting")} testId="nav-sorting" />
          )}

          {hasModuleAccess(user, "tms") && (
            <NavItem to="/tms" icon={Truck} label="Transport" testId="nav-tms" />
          )}

          {hasModuleAccess(user, "engineering") && (
            <NavItem to="/engineering" icon={Wrench} label="Engineering" testId="nav-engineering" />
          )}

          {hasModuleAccess(user, "stock_lock") && (
            <NavItem to="/stock-locks" icon={Lock} label="Stock Locks" testId="nav-stock-locks" />
          )}

          {hasModuleAccess(user, "material_movement") && (
            <NavItem to="/material-movements" icon={PackagePlus} label="Material Movements" testId="nav-material-movements" />
          )}

          {minResponder && (
            <NavItem to="/logistics-requests" icon={FileText} label="Freight Deals" testId="nav-logistics-requests" />
          )}

          {/* Iter143/144/145 — Időkapu-foglaló (Dock Time-Slot Booking) sidebar section.
              Positioned directly under Freight Deals (iter145 · B1) so booking + calendar
              live in the same eye-scan zone as the rest of the freight ops flow.
              Visibility is driven by USER ROLE only (iter144); the "Beállítások" sub-item
              is the sole entry that still requires the caller's tenant to be a plant. */}
          {hasDockModule && (
            <>
              <SectionLabel>Időkapu-foglaló</SectionLabel>
              <div data-testid="nav-dock-scheduling">
                {hasDockAdminConfig && (
                  <NavItem to="/admin/dock-scheduling" icon={Sliders} label="Beállítások" testId="nav-dock-settings" />
                )}
                <NavItem to="/dock-scheduling/calendar" icon={CalendarClock} label="Naptár" testId="nav-dock-calendar" />
                {hasDockAdminRole && (
                  <NavItem to="/dock-scheduling/gate" icon={ScanLine} label="Kapu-szkenner" testId="nav-dock-gate" />
                )}
                {hasDockAdminRole && (
                  <NavItem to="/dock-scheduling/search" icon={SearchIcon} label="Keresés" testId="nav-dock-search" />
                )}
                {hasDockBooker && (
                  <NavItem to="/dock-scheduling/book" icon={DoorOpen} label="Foglalás" testId="nav-dock-book" />
                )}
                {hasDockBooker && (
                  <NavItem to="/dock-scheduling/my-bookings" icon={Inbox} label="Foglalásaim" testId="nav-dock-mybookings" />
                )}
              </div>
            </>
          )}

          {minResponder && (
            <NavItem to="/approvals" icon={Inbox} label="Approvals" testId="nav-approvals" />
          )}

          {(hasModuleAccess(user, "chain") || hasModuleAccess(user, "tenants")) && (
            <SectionLabel>{t("nav.section_supply_chain")}</SectionLabel>
          )}
          {hasModuleAccess(user, "chain") && (
            <NavItem to="/chain" icon={Network} label={t("nav.chain_map")} testId="nav-chain-map" />
          )}
          {hasModuleAccess(user, "tenants") && (
            <NavItem to="/tenants" icon={Building2} label={t("nav.tenants")} badge="16" badgeColor="grey" testId="nav-tenants" />
          )}

          {(hasModuleAccess(user, "analytics") || (minResponder && (hasModuleAccess(user, "fmea") || hasModuleAccess(user, "8d") || hasModuleAccess(user, "lpa")))) && (
            <SectionLabel>{t("nav.section_reporting")}</SectionLabel>
          )}
          {hasModuleAccess(user, "analytics") && (
            <NavItem to="/analytics" icon={LineChart} label={t("nav.analytics")} testId="nav-analytics-&-mttr" />
          )}
          {minResponder && (
            <>
              {hasModuleAccess(user, "fmea") && <NavItem to="/fmea" icon={Table} label={t("nav.fmea")} testId="nav-fmea-module" />}
              {hasModuleAccess(user, "8d") && <NavItem to="/8d" icon={ClipboardList} label={t("nav.eightd")} testId="nav-8d-reports" />}
              {hasModuleAccess(user, "lpa") && <NavItem to="/lpa" icon={ShieldCheck} label={t("nav.lpa")} testId="nav-lpa-audits" />}
            </>
          )}
          <NavItem to="/audit-trail" icon={History} label={t("nav.audit_trail")} testId="nav-audit-trail" />
          <NavItem to="/notifications" icon={Bell} label={t("nav.notifications")} testId="nav-notification-log" />
          {/* H.1 — BI dashboard. Forwarder-internal only; route-guard
              prevents customer-tier users from reaching it. */}
          <NavItem to="/bi/dashboard" icon={LineChart} label={t("nav.bi_dashboard") || "BI Dashboard"} testId="nav-bi-dashboard" />

          <SectionLabel>{t("nav.section_system")}</SectionLabel>
          <NavItem to="/settings" icon={SettingsIcon} label={t("nav.settings")} testId="nav-settings" />

          {(isPlatformAdmin || role === "tenant_admin") && (
            <>
              <SectionLabel accent="#f3b14a">{t("nav.section_admin")}</SectionLabel>
              <AdminNavItem to="/admin" icon={ShieldAlert} label="Admin" testId="nav-admin" />
              {isPlatformAdmin && (
                <AdminNavItem to="/admin/users" icon={Users} label={t("nav.users")} testId="nav-users" />
              )}
              {isPlatformAdmin && (
                <AdminNavItem to="/admin/analytics" icon={LineChart} label={t("nav.user_analytics") || "User Analytics"} testId="nav-user-analytics" />
              )}
              {isPlatformAdmin && (
                <AdminNavItem to="/admin/customer-relationships" icon={GitBranch} label="Customer Relationships" testId="nav-customer-relationships" />
              )}
              {isPlatformAdmin && (
                <AdminNavItem to="/admin/plant-grants" icon={ShieldCheck} label="Plant Grants" testId="nav-plant-grants" />
              )}
              {/* Iter143 — Customer Portal deep-link. Customer-tier users are
                  auto-routed to /portal/* and never see this sidebar; the
                  ProtectedRoute redirects any non-customer visitor away. So
                  the entry is only meaningful for platform_admin, who is
                  exempt from that redirect and needs it for testing/support. */}
              {isPlatformAdmin && (
                <AdminNavItem to="/portal/my-requests" icon={UserCheck} label="Customer Portal" testId="nav-customer-portal" />
              )}
            </>
          )}
        </nav>
        <RiskFooter />
      </aside>
    </>
  );
};
